import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import {KeyboardDatePicker, KeyboardTimePicker} from '@material-ui/pickers';

import {forceTimezone} from 'common/utils/time';
import {getDays, getTimes} from 'ondemand/utils/gtfs';

import styles from './DateTimeModal.css';


const formatDate = date => moment(date).format('MMM D, YYYY');

export const _formatDate = formatDate; // unit tests

/* A constant to represent "now".
 * The API representation is `null`, but we need some constant
 * to show that the user has actually set a pickup time.
 */
export const CURRENT_TIME = 'current_time';

/* A ride can be booked for "now" or "later". */
const BOOKING = {
  NOW: 'use-now',
  LATER: 'use-later',
};

class DateTimeModal extends React.Component {
  static displayName = 'DateTimeModal';

  static propTypes = {
    allowBookForNow: PropTypes.bool.isRequired,
    allowBookForLater: PropTypes.bool.isRequired,
    initialValue: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.string,
    ]),
    message: PropTypes.node,
    onBack: PropTypes.func.isRequired,
    onContinue: PropTypes.func.isRequired,
    service: PropTypes.object,
    timezone: PropTypes.string,
  };

  state = {
    pickupTime: null,
    bookFor: null,
  };

  componentDidMount() {
    if (this.props.initialValue && this.props.initialValue !== CURRENT_TIME) {
      this.setState({pickupTime: this.props.initialValue});
    } else {
      this.setState({pickupTime: moment.tz(this.props.timezone)});
    }

    if (this.props.allowBookForNow) {
      this.setState({
        bookFor: BOOKING.NOW,
      });
    } else if (this.props.allowBookForLater) {
      this.setState({
        bookFor: BOOKING.LATER,
      });
    }
  }

  _shouldDisableDate = (day) => {
    const dt = forceTimezone(this.props.timezone, moment(day));
    const today = moment.tz(this.props.timezone).startOf('day');

    // No service can be booked for before today.
    if (dt.isBefore(today)) {
      return true;
    }

    // set max date based on service settings
    if (this.props.service.in_advance_enabled && this.props.service.max_schedule_time) {
      const maxDate = today.add(parseInt(this.props.service.max_schedule_time, 10), 'days');
      if (dt.isAfter(maxDate)) {
        return true;
      }
    }

    // set max date based on service end
    if (this.props.service.end_date) {
      const maxDate = moment(this.props.service.end_date, 'YYYY-MM-DD')
        .tz(this.props.timezone).startOf('day');
      if (dt.isAfter(maxDate)) {
        return true;
      }
    }

    return !this.props.service.inServiceOn(day, this.props.timezone);
  };

  render() {
    const {
      allowBookForLater,
      allowBookForNow,
      message,
      onBack,
      onContinue,
      service,
    } = this.props;

    if (!allowBookForNow && !allowBookForLater) {
      // Somehow, the user has chosen a service that isn't currently running.
      return (
        <Dialog open>
          <div data-id={'service-unavailable-modal-container'} >
            <DialogTitle>Service Unavailable</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Sorry, {service.name} is not running.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                data-id="back-button"
                onClick={onBack}
                color="primary"
              >
                Back
              </Button>
            </DialogActions>
          </div>
        </Dialog>
      );
    }

    let dt = null;
    let isValid = false;
    if (this.state.bookFor === BOOKING.NOW) {
      dt = CURRENT_TIME;
      isValid = true;
    } else if (this.state.pickupTime) {
      dt = this.state.pickupTime;
      isValid = service.inServiceAt(dt);
    }

    return (
      <Dialog open>
        <div data-id={'pickup-time-modal-container'} >
          <DialogTitle>Select Pickup Time</DialogTitle>
          <DialogContent>
            <div className={styles.root}>
              {allowBookForNow && !allowBookForLater &&
                <div>
                  Your ride will be scheduled for the next available time.
                </div>
              }
              {message && <div className={styles.message}>{message}</div>}
              {allowBookForNow && allowBookForLater &&
                <RadioGroup
                  data-id={this.state.bookFor}
                  name="bookFor"
                  onChange={(event, value) => this.setState({bookFor: value})}
                  value={this.state.bookFor}
                >
                  <FormControlLabel
                    control={<Radio />}
                    label="Next available"
                    value={BOOKING.NOW}
                  />
                  <FormControlLabel
                    control={<Radio />}
                    label="Specify time..."
                    value={BOOKING.LATER}
                  />
                </RadioGroup>
              }
              {this.state.bookFor === BOOKING.LATER &&
                <div style={allowBookForNow && allowBookForLater ? {marginTop: '24px'} : null}>
                  <div className={styles.serviceTimesMessage}>
                    {service.name} is available {getDays(service)} from {getTimes(service)}.
                  </div>
                  <div className={styles.dateTimeContainer}>
                    <KeyboardDatePicker
                      data-id="pickup-time-modal-date-input"
                      format="MM-DD-YYYY"
                      fullWidth
                      label="Date"
                      margin="normal"
                      onChange={(date) => this.setState({
                        pickupTime: date,
                      })}
                      shouldDisableDate={this._shouldDisableDate}
                      value={this.state.pickupTime}
                    />
                    <KeyboardTimePicker
                      data-id="pickup-time-modal-time-input"
                      error={this.state.pickupTime && !isValid}
                      helperText={_.all([
                        this.state.pickupTime &&
                        !isValid,
                      ]) ? 'Service is not available at this time.' : null}
                      fullWidth
                      label="Time"
                      margin="normal"
                      onChange={(time) => this.setState({
                        pickupTime: time,
                      })}
                      title="Please choose a date before selecting a time."
                      value={this.state.pickupTime}
                    />
                  </div>
                </div>
              }
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              data-id="pickup-time-modal-back-button"
              onClick={onBack}
            >
              Back
            </Button>
            <Button
              color="primary"
              data-id="pickup-time-modal-continue-button"
              disabled={!isValid}
              onClick={() => onContinue(dt)}
            >
              Continue
            </Button>
          </DialogActions>
        </div>
      </Dialog>
    );
  }
}

export default DateTimeModal;



// WEBPACK FOOTER //
// ./src/ondemand/components/DateTimeModal/DateTimeModal.js